import { formatUsPhone, normalizeUsPhone } from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from '@mevaser/ui'
import { Delete, Grid3x3 } from 'lucide-react'
import { useState } from 'react'
import { CallButton } from './call-button.js'
import { useSoftphone } from './softphone-provider.js'

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0'] as const

/**
 * Header control for dialing any number by typing it (t1414). Calls go through
 * the same browser calling and call bar as the Call button, and the server
 * files them on the matching support ticket the same way. Hidden when browser
 * calling isn't available, like the other softphone controls.
 */
export function DialpadButton() {
  const { available, state, placeCall } = useSoftphone()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')

  if (!available) return null

  const normalized = normalizeUsPhone(value)
  const close = () => {
    setOpen(false)
    setValue('')
  }
  const clean = (raw: string) => raw.replace(/[^\d+]/g, '').slice(0, 16)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setValue('')
      }}
    >
      <Button
        variant="ghost"
        size="icon"
        aria-label="Dial a number"
        disabled={state !== 'idle'}
        onClick={() => setOpen(true)}
      >
        <Grid3x3 />
      </Button>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>Dial a number</DialogTitle>
          <DialogDescription>
            {normalized ? formatUsPhone(normalized) : 'Type or tap a 10-digit US number.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          <Input
            type="tel"
            inputMode="tel"
            autoFocus
            aria-label="Phone number"
            placeholder="(718) 555-0143"
            value={value}
            onChange={(e) => setValue(clean(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && normalized) {
                e.preventDefault()
                void placeCall(normalized)
                  .then(close)
                  .catch(() => toast.error('Could not place the call'))
              }
            }}
          />
          <div className="grid grid-cols-3 gap-2">
            {KEYS.map((key) => (
              <Button
                key={key}
                type="button"
                variant="secondary"
                onClick={() => setValue((v) => clean(v + key))}
              >
                {key}
              </Button>
            ))}
            <Button
              type="button"
              variant="secondary"
              aria-label="Delete last digit"
              onClick={() => setValue((v) => v.slice(0, -1))}
            >
              <Delete />
            </Button>
          </div>
        </DialogBody>
        <DialogFooter>
          <CallButton
            phone={normalized ?? ''}
            label="Call"
            disabled={!normalized}
            onStart={close}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
